import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../../contexts/AuthContext'
import {
  Button,
  SettingsGroup,
  ChangePasswordModal,
  ConfirmationModal,
  Input,
} from '@airclou/ui'
import {
  RectangleEllipsis,
  QrCode,
  CheckCircle2,
} from 'lucide-react'
import { dashboardApiClient } from '../../../services/api'
import { TwoFactorSetupModal } from '../components/TwoFactorSetupModal'
import { useTimezone } from '@airclou/shell/timezone'
import { formatDateInTz } from '@airclou/utils'

/**
 * Server reasons this page can put on a specific field. Anything not listed
 * falls through to the modal's general error line. Matched on `code`, never on
 * the message text — the message is the auth service's English copy.
 */
const PASSWORD_FIELD_ERRORS: Record<string, 'currentPassword' | 'newPassword'> = {
  INVALID_CURRENT_PASSWORD: 'currentPassword',
  PASSWORD_TOO_SHORT: 'newPassword',
  PASSWORD_TOO_LONG: 'newPassword',
  PASSWORD_TOO_WEAK: 'newPassword',
  PASSWORD_RECENTLY_USED: 'newPassword',
  PASSWORD_UNCHANGED: 'newPassword',
}

export function CredentialsTab() {
  const timezone = useTimezone()
  const { t } = useTranslation('dashboard')
  const { user } = useAuth()

  // Password modal state
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [passwordErrors, setPasswordErrors] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  const [isChangingPassword, setIsChangingPassword] = useState(false)
  const [passwordChangeStatus, setPasswordChangeStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [passwordChangeError, setPasswordChangeError] = useState<string | null>(null)
  // Read from the security activity log rather than stated as fact: the page
  // used to print a fixed date that had nothing to do with this account.
  // `undefined` = still loading, `null` = no recorded change.
  const [lastPasswordChange, setLastPasswordChange] = useState<string | null | undefined>(undefined)

  // Two-Factor Authentication state.
  //
  // `undefined` = not read yet, so the row shows neither "on" nor "off" before
  // the server has answered — stating one and then flipping is worse than
  // saying nothing for a moment.
  const [show2FAWizard, setShow2FAWizard] = useState(false)
  const [twoFAEnabled, setTwoFAEnabled] = useState<boolean | undefined>(undefined)
  const [twoFAError, setTwoFAError] = useState<string | null>(null)
  // A read that failed is not the same as "off", and not the same as still
  // loading. Without this third state the button stayed disabled forever beside
  // an empty description — a dead end nothing but a page reload escaped.
  const [twoFAStatusFailed, setTwoFAStatusFailed] = useState(false)
  const [isEnrolling, setIsEnrolling] = useState(false)
  // Kept apart from twoFAError on purpose: this one belongs to the modal. Sharing
  // one variable put "your password is incorrect" on the row behind the modal,
  // and left it there as the two-factor status after the modal was dismissed.
  const [disableError, setDisableError] = useState<string | null>(null)
  const [showDisableModal, setShowDisableModal] = useState(false)
  const [disablePassword, setDisablePassword] = useState('')
  const [isDisabling, setIsDisabling] = useState(false)
  const [twoFAData, setTwoFAData] = useState({
    secretKey: '',
    qrCodeUrl: '',
    backupCodes: [] as string[],
  })

  // The auth service records a `password_changed` event on every successful
  // change, so that log is the source of truth for "last changed". Failed
  // attempts are logged under the same type, hence the status filter — and
  // hence the page size: the endpoint cannot filter by status, so a run of
  // recent failures pushes the success that matters off the end and the page
  // then says the password has never been changed. 100 is the endpoint's own
  // cap, which is twenty lockout cycles' worth of failures.
  const loadLastPasswordChange = async () => {
    try {
      const { events } = await dashboardApiClient.getSecurityEvents({ event_type: 'password_changed', limit: 100 })
      const succeeded = events.find(e => e.status === 'success')
      setLastPasswordChange(succeeded ? succeeded.created_at : null)
    } catch {
      // The date is background detail; a failed read leaves it unstated rather
      // than blocking the Change Password button beside it.
      setLastPasswordChange(null)
    }
  }

  /** Whether the account actually has a second factor, from the server. */
  const loadMfaStatus = async () => {
    try {
      const status = await dashboardApiClient.getMfaStatus()
      setTwoFAEnabled(status.enabled)
      setTwoFAStatusFailed(false)
      setTwoFAError(null)
    } catch {
      // Left unstated rather than claimed "off": telling someone their second
      // factor is off when the read merely failed is the worse error. But say
      // that it failed — an empty row beside a dead button explains nothing.
      setTwoFAEnabled(undefined)
      setTwoFAStatusFailed(true)
      setTwoFAError(t('security.twoFactorCheckFailed'))
    }
  }

  useEffect(() => {
    loadLastPasswordChange()
    loadMfaStatus()
  }, [])

  const handleChangePassword = () => {
    setShowChangePasswordModal(true)
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setPasswordErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setPasswordChangeStatus('idle')
    setPasswordChangeError(null)
  }

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (passwordErrors[field as keyof typeof passwordErrors]) {
      setPasswordErrors(prev => ({ ...prev, [field]: '' }))
    }
    if (passwordChangeStatus === 'error') {
      setPasswordChangeStatus('idle')
      setPasswordChangeError(null)
    }
  }

  const validatePassword = () => {
    const errors = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
    
    let isValid = true

    if (!passwordData.currentPassword) {
      errors.currentPassword = t('security.currentPasswordRequired')
      isValid = false
    }

    if (!passwordData.newPassword) {
      errors.newPassword = t('security.newPasswordRequired')
      isValid = false
    } else if (passwordData.newPassword.length < 8) {
      errors.newPassword = t('security.passwordMinLength')
      isValid = false
    } else if (passwordData.newPassword.length > 128) {
      errors.newPassword = t('security.passwordMaxLength')
      isValid = false
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9])/.test(passwordData.newPassword)) {
      // Mirrors validatePasswordStrength in the auth service
      // (utils/passwordPolicy.ts) — length floor, length ceiling and character
      // classes alike. Checking less here just moves the rejection to the
      // server after the operator has typed the password three times.
      errors.newPassword = t('security.passwordComplexity')
      isValid = false
    }

    if (!passwordData.confirmPassword) {
      errors.confirmPassword = t('security.confirmPasswordRequired')
      isValid = false
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = t('security.passwordsDoNotMatch')
      isValid = false
    }

    if (passwordData.currentPassword === passwordData.newPassword) {
      errors.newPassword = t('security.passwordMustDiffer')
      isValid = false
    }

    setPasswordErrors(errors)
    return isValid
  }

  /** The message for a server-named failure, or null to use the general line. */
  const passwordErrorMessage = (code: string): string | null => {
    switch (code) {
      case 'INVALID_CURRENT_PASSWORD': return t('security.incorrectPassword')
      case 'PASSWORD_RECENTLY_USED': return t('security.passwordRecentlyUsed')
      case 'PASSWORD_UNCHANGED': return t('security.passwordMustDiffer')
      case 'PASSWORD_TOO_SHORT': return t('security.passwordMinLength')
      case 'PASSWORD_TOO_LONG': return t('security.passwordMaxLength')
      case 'PASSWORD_TOO_WEAK': return t('security.passwordComplexity')
      case 'TOO_MANY_ATTEMPTS': return t('security.tooManyPasswordAttempts')
      case 'NO_PASSWORD_CREDENTIAL': return t('security.noPasswordCredential')
      case 'IMPERSONATION_FORBIDDEN': return t('security.impersonationCannotChangePassword')
      default: return null
    }
  }

  const confirmPasswordChange = async () => {
    if (!validatePassword()) return

    setIsChangingPassword(true)
    setPasswordChangeStatus('idle')
    setPasswordChangeError(null)

    try {
      await dashboardApiClient.changePassword(passwordData.currentPassword, passwordData.newPassword)

      setPasswordChangeStatus('success')
      // The change is stored, so the date beside the button is now stale.
      loadLastPasswordChange()
      setTimeout(() => {
        setShowChangePasswordModal(false)
        setPasswordChangeStatus('idle')
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      }, 2000)
    } catch (error) {
      // The api client attaches `.code` to everything the server answered, so
      // its presence is what separates "the server refused" from "I could not
      // reach the server" — a timeout or a dropped connection arrives here as a
      // bare AbortError with no code. Those must not be reported as if the
      // password were rejected, and their raw text ("signal timed out") must
      // not reach the operator.
      const serverCode = (error as { code?: string }).code
      const code = serverCode || 'CHANGE_PASSWORD_FAILED'
      const message = passwordErrorMessage(code)

      // Put the reason on the field it belongs to where there is one, so the
      // operator sees which box to fix — and then leave the banner off, rather
      // than printing the same sentence twice on one small modal.
      const field = PASSWORD_FIELD_ERRORS[code]
      if (field && message) {
        setPasswordErrors(prev => ({ ...prev, [field]: message }))
        setPasswordChangeStatus('idle')
      } else {
        // Nothing to attach it to — "too many attempts", an SSO-only account, a
        // request that never landed. Those belong in the banner.
        setPasswordChangeStatus('error')
        // Wording, in order of preference: this page's own for the codes it
        // knows; the server's own sentence for a code it does not (
        // IDENTITY_MISMATCH tells the operator to reload, which a generic line
        // does not); and the network line when nothing answered at all.
        setPasswordChangeError(
          message
            ?? (serverCode
              ? (error instanceof Error && error.message ? error.message : t('security.failedToChangePassword'))
              : t('security.networkError')),
        )
      }
      // Deliberately not auto-cleared: the error says what to do differently,
      // and it disappearing after three seconds took that away mid-read. It
      // clears when the operator edits a field, or closes the modal.
    } finally {
      setIsChangingPassword(false)
    }
  }

  const cancelPasswordChange = () => {
    setShowChangePasswordModal(false)
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setPasswordErrors({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    })
    setPasswordChangeStatus('idle')
    setPasswordChangeError(null)
  }

  const handle2FASetup = async () => {
    setTwoFAError(null)
    setIsEnrolling(true)
    try {
      // Secret generated and QR rendered by the auth service. This page used to
      // do both itself — Math.random() for the secret, and handing it to
      // api.qrserver.com to be drawn, which put every enrolling user's second
      // factor into a third party's request logs.
      const { secret, qrDataUrl } = await dashboardApiClient.enrollMfa()
      setTwoFAData({ secretKey: secret, qrCodeUrl: qrDataUrl, backupCodes: [] })
      setShow2FAWizard(true)
    } catch (error) {
      const code = (error as { code?: string }).code
      setTwoFAError(
        code === 'MFA_ALREADY_ENABLED'
          ? t('security.twoFactorAlreadyOn')
          : t('security.twoFactorSetupFailed'),
      )
    } finally {
      setIsEnrolling(false)
    }
  }

  /**
   * Prove a code and turn the factor on.
   *
   * Returns the backup codes so the wizard can show them once — they are
   * bcrypt-hashed server-side and nothing can recover them afterwards.
   */
  const verify2FACode = async (code: string): Promise<string[]> => {
    const { backupCodes } = await dashboardApiClient.activateMfa(code)
    setTwoFAData(prev => ({ ...prev, backupCodes }))
    return backupCodes
  }

  const handle2FADisable = () => {
    setDisableError(null)
    setDisablePassword('')
    setShowDisableModal(true)
  }

  const confirmDisable2FA = async () => {
    setIsDisabling(true)
    setDisableError(null)
    try {
      // The password, not just the session: holding a signed-in tab must not be
      // enough to strip a second factor off the account.
      await dashboardApiClient.disableMfa(disablePassword)
      setShowDisableModal(false)
      setDisablePassword('')
      await loadMfaStatus()
    } catch (error) {
      const code = (error as { code?: string }).code
      if (code === 'MFA_NOT_ENABLED') {
        // Already off — the row was stale, not the request wrong. Reporting a
        // failure here would be false: the state the operator asked for is the
        // state the account is in.
        setShowDisableModal(false)
        setDisablePassword('')
        await loadMfaStatus()
        return
      }
      setDisableError(
        code === 'INVALID_CURRENT_PASSWORD' ? t('security.incorrectPassword')
        : code === 'TOO_MANY_ATTEMPTS' ? t('security.tooManyPasswordAttempts')
        // An SSO account has no password to prove, so this form can never
        // succeed for them. Saying "try again" invites exactly the loop of
        // correct-but-refused attempts that the rate limit then punishes.
        : code === 'NO_PASSWORD_CREDENTIAL' ? t('security.noPasswordDisableTwoFactor')
        : t('security.twoFactorDisableFailed'),
      )
    } finally {
      setIsDisabling(false)
    }
  }

  const complete2FASetup = async () => {
    setShow2FAWizard(false)
    setTwoFAData({ secretKey: '', qrCodeUrl: '', backupCodes: [] })
    // Read it back rather than assuming: the row on the server is what decides
    // whether the next sign-in asks for a code.
    await loadMfaStatus()
  }

  const cancel2FASetup = async () => {
    setShow2FAWizard(false)
    setTwoFAData({ secretKey: '', qrCodeUrl: '', backupCodes: [] })
    // Re-read rather than assume nothing happened. Closing the wizard BEFORE
    // the code is proven leaves a pending row that is not a second factor, and
    // starting again replaces it — but the wizard can also be closed on its
    // last step, which is after activation has already succeeded. Assuming
    // "cancelled means unchanged" left the row reading "off" on an account that
    // would demand a code at the next sign-in.
    await loadMfaStatus()
  }

  return (
    <>
      <SettingsGroup
        sections={[
          {
            icon: RectangleEllipsis,
            title: t('security.password'),
            description: lastPasswordChange === undefined
              ? ''
              : lastPasswordChange === null
                ? t('security.passwordNeverChanged')
                : t('security.lastChanged', { date: formatDateInTz(lastPasswordChange, timezone) }),
            action: (
              <Button variant="outline" size="sm" onClick={handleChangePassword}>
                {t('security.changePassword')}
              </Button>
            ),
          },
          {
            icon: QrCode,
            title: t('security.twoFactor'),
            // An error wins; otherwise blank until the server has answered.
            // Saying "disabled" and then flipping to "enabled" a moment later
            // tells the operator something false about their own security,
            // however briefly. And the error has to win everywhere, not only
            // while the status is unknown: a failed enrolment happens once the
            // status IS known, so the first cut set the message and then
            // rendered nothing at all.
            description: twoFAError
              ? twoFAError
              : twoFAEnabled === undefined
                ? ''
                : t('security.twoFactorStatus', { status: twoFAEnabled ? t('security.enabled') : t('security.disabled') }),
            action: (
              <>
                {twoFAEnabled === true && (
                  <CheckCircle2 className="h-4 w-4 text-green-500" />
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(twoFAEnabled === undefined && !twoFAStatusFailed) || isEnrolling}
                  onClick={twoFAEnabled ? handle2FADisable : handle2FASetup}
                >
                  {isEnrolling
                    ? t('common.loading')
                    : twoFAEnabled ? t('security.disable') : t('security.enable')}
                </Button>
              </>
            ),
          },
        ]}
      />

      {/* Change Password Modal */}
      {showChangePasswordModal && (
        <ChangePasswordModal
          isOpen={showChangePasswordModal}
          onClose={cancelPasswordChange}
          onSubmit={confirmPasswordChange}
          onCancel={cancelPasswordChange}
          isLoading={isChangingPassword}
          passwordData={passwordData}
          passwordErrors={passwordErrors}
          onPasswordChange={handlePasswordChange}
          passwordChangeStatus={passwordChangeStatus}
          errorMessage={passwordChangeError ?? undefined}
          passwordHint={t('security.passwordRequirements')}
        />
      )}

      {/* Two-Factor Authentication Setup Modal */}
      <TwoFactorSetupModal
        isOpen={show2FAWizard}
        onClose={cancel2FASetup}
        onComplete={complete2FASetup}
        onVerify={verify2FACode}
        qrCodeUrl={twoFAData.qrCodeUrl}
        secretKey={twoFAData.secretKey}
        backupCodes={twoFAData.backupCodes}
        userEmail={user?.email || ''}
      />

      {/* Turning it off needs the password, so it gets a confirmation of its own. */}
      <ConfirmationModal
        isOpen={showDisableModal}
        onClose={() => !isDisabling && setShowDisableModal(false)}
        title={t('security.disableTwoFactorTitle')}
        variant="warning"
        confirmText={t('security.disable')}
        cancelText={t('common.cancel')}
        onConfirm={confirmDisable2FA}
        onCancel={() => !isDisabling && setShowDisableModal(false)}
        isLoading={isDisabling}
        confirmDisabled={!disablePassword}
      >
        <div className="space-y-3">
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {t('security.disableTwoFactorWarning')}
          </p>
          <Input
            type="password"
            value={disablePassword}
            onChange={setDisablePassword}
            placeholder={t('security.currentPassword')}
            disabled={isDisabling}
          />
          {disableError && (
            <p className="text-sm text-red-600 dark:text-red-400">{disableError}</p>
          )}
        </div>
      </ConfirmationModal>
    </>
  )
}
